import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Briefcase, Loader2, Sparkles, Plus, ArrowRight, MapPin, Building2, DollarSign,
    Globe2, Clock3, CheckCircle2, XCircle, Zap, Archive, FileEdit, Trash2, RotateCcw, Users2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const _fmtCountdown = (targetIso) => {
    if (!targetIso) return "";
    const ms = new Date(targetIso).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m ${String(s).padStart(2, "0")}s left`;
};

const StatusPill = ({ status }) => {
    const style = status === "ready" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" :
        status === "processing" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" :
        "bg-destructive/10 text-destructive";
    return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${style}`}>{status}</span>;
};

const boardStatusStyle = {
    published: { icon: CheckCircle2, className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", label: "Live" },
    pending_review: { icon: Clock3, className: "bg-amber-500/15 text-amber-600 dark:text-amber-400", label: "Pending review" },
    rejected: { icon: XCircle, className: "bg-rose-500/15 text-rose-600 dark:text-rose-400", label: "Rejected" },
    draft: { icon: FileEdit, className: "bg-slate-500/15 text-slate-600 dark:text-slate-400", label: "Draft" },
    archived: { icon: Archive, className: "bg-slate-500/15 text-slate-500 dark:text-slate-400", label: "Archived" },
};

const BoardStatusPill = ({ status }) => {
    const meta = boardStatusStyle[status] || { icon: Clock3, className: "bg-muted text-muted-foreground", label: status || "Draft" };
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.className}`}>
            <Icon size={10} /> {meta.label}
        </span>
    );
};

const FILTER_TABS = [
    { key: "all", label: "All" },
    { key: "published", label: "Live" },
    { key: "pending_review", label: "Pending" },
    { key: "draft", label: "Drafts" },
    { key: "archived", label: "Archived" },
    { key: "rejected", label: "Rejected" },
];

const EmployerJobs = () => {
    const [briefs, setBriefs] = useState([]);
    const [board, setBoard] = useState([]);
    const [pendingUndo, setPendingUndo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [promoting, setPromoting] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [busyId, setBusyId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null); // {id, title}
    const [confirmText, setConfirmText] = useState("");
    // Bulk-select state — Set<jobId>. Reset on load + filter change.
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    // Tick every 30s so the undo banners' countdown chips stay live.
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [params] = useSearchParams();
    const highlightedJob = params.get("highlight");
    const openedDigestJob = useRef(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [briefsRes, boardRes] = await Promise.all([
                api.get("/employer/briefs"),
                api.get("/employer/jobs/mine").catch(() => ({ data: { results: [], pending_undo: [] } })),
            ]);
            setBriefs(briefsRes.data || []);
            setBoard(boardRes.data?.results || []);
            setPendingUndo(boardRes.data?.pending_undo || []);
            setSelected(new Set());
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Countdown tick only while there is at least one pending-undo card,
    // and drop the tick when they've all expired to avoid useless renders.
    useEffect(() => {
        if (pendingUndo.length === 0) return;
        const t = setInterval(() => setNowMs(Date.now()), 30_000);
        return () => clearInterval(t);
    }, [pendingUndo.length]);
    // Reference nowMs in a render-time noop so React re-renders on tick.
    const _tickTag = `t-${nowMs}`;

    // Handle Stripe redirect back after promoting.
    useEffect(() => {
        if (params.get("promoted")) {
            toast.success("Payment received — your job is now Featured for 7 days.");
        } else if (params.get("promote_canceled")) {
            toast.info("Promotion canceled. You can try again anytime.");
        }
    }, [params]);

    const filteredBoard = useMemo(() => {
        if (statusFilter === "all") return board;
        return board.filter((j) => (j.status || "pending_review") === statusFilter);
    }, [board, statusFilter]);

    const counts = useMemo(() => {
        const c = { all: board.length };
        for (const j of board) {
            const s = j.status || "pending_review";
            c[s] = (c[s] || 0) + 1;
        }
        return c;
    }, [board]);

    // iter 149.4 — Pay-mode analytics. Count how many of the employer's
    // active/live postings disclose a full range vs "starting at" vs DOE.
    // Only surface the nudge when at least one DOE posting exists AND
    // there's a non-zero total so the badge isn't confusing for
    // employers who've never posted a role.
    const payModeStats = useMemo(() => {
        const live = board.filter((j) => (j.status || "pending_review") !== "archived");
        const total = live.length;
        let range = 0, starting_at = 0, doe = 0;
        for (const j of live) {
            const m = j.pay_mode || "range";
            if (m === "range") range += 1;
            else if (m === "starting_at") starting_at += 1;
            else if (m === "doe") doe += 1;
        }
        return { total, range, starting_at, doe };
    }, [board]);

    // iter 149.5 — Bulk "Add a range" modal state. The rows array is
    // keyed by job_id and holds the min/max the employer is typing.
    const [bulkPayOpen, setBulkPayOpen] = useState(false);
    const [bulkPayRows, setBulkPayRows] = useState({}); // { [job_id]: {min, max} }
    const [bulkPaySaving, setBulkPaySaving] = useState(false);
    const doeJobs = useMemo(
        () => board.filter((j) => (j.pay_mode || "range") === "doe" && (j.status || "pending_review") !== "archived")
            .sort((a, b) => Number(b.id === highlightedJob) - Number(a.id === highlightedJob)),
        [board, highlightedJob],
    );
    useEffect(() => {
        if (!loading && highlightedJob && openedDigestJob.current !== highlightedJob && doeJobs.some(j => j.id === highlightedJob)) {
            openedDigestJob.current = highlightedJob;
            setBulkPayOpen(true);
        }
    }, [loading, highlightedJob, doeJobs]);
    // Reset the inputs whenever the modal opens fresh.
    useEffect(() => {
        if (bulkPayOpen) {
            const init = {};
            for (const j of doeJobs) init[j.id] = { min: "", max: "" };
            setBulkPayRows(init);
        }
    }, [bulkPayOpen, doeJobs]);

    const saveBulkPay = async () => {
        // Build the updates list — only rows the employer actually filled.
        const updates = [];
        for (const j of doeJobs) {
            const row = bulkPayRows[j.id] || {};
            const min = parseInt(row.min, 10);
            const max = parseInt(row.max, 10);
            if (!min || !max) continue;  // silently skip empty rows
            updates.push({ job_id: j.id, pay_mode: "range", salary_min: min, salary_max: max });
        }
        if (!updates.length) {
            toast.error("Enter a min and max for at least one posting.");
            return;
        }
        setBulkPaySaving(true);
        try {
            const { data } = await api.post("/employer/jobs/bulk-pay-mode", { updates });
            const t = data.tally || {};
            const bits = [];
            if (t.updated) bits.push(`${t.updated} converted to range`);
            if (t.invalid) bits.push(`${t.invalid} skipped (validation)`);
            if (t.not_found) bits.push(`${t.not_found} not found`);
            toast.success(bits.length ? bits.join(" · ") : "No changes.");
            // Merge the new pay_mode back onto local board state so the
            // analytics badge updates without a full refetch.
            const updatedById = Object.fromEntries(
                (data.results || [])
                    .filter((r) => r.outcome === "updated")
                    .map((r) => [r.job_id, r]),
            );
            setBoard((rows) => rows.map((r) => {
                const hit = updatedById[r.id];
                const submitted = updates.find((u) => u.job_id === r.id);
                if (hit && submitted) {
                    return { ...r, pay_mode: "range",
                             salary_min: submitted.salary_min, salary_max: submitted.salary_max };
                }
                return r;
            }));
            setBulkPayOpen(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBulkPaySaving(false);
        }
    };

    const promote = async (jobId) => {
        setPromoting(jobId);
        try {
            const { data } = await api.post(`/employer/jobs/${jobId}/promote`);
            if (data?.url) window.location.href = data.url;
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setPromoting(null); }
    };

    const setJobStatus = async (job, newStatus) => {
        setBusyId(job.id);
        try {
            await api.patch(`/employer/jobs/${job.id}/status`, { status: newStatus });
            toast.success(newStatus === "draft" ? "Moved to Draft." : "Job archived.");
            setBoard((rows) => rows.map((r) => r.id === job.id ? { ...r, status: newStatus } : r));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusyId(null); }
    };

    const requestDelete = (job) => { setDeleteTarget(job); setConfirmText(""); };
    const confirmDelete = async () => {
        if (confirmText.trim() !== "DELETE") return;
        const job = deleteTarget;
        setBusyId(job.id);
        try {
            const { data } = await api.delete(`/employer/jobs/${job.id}`);
            // Soft-delete: card leaves the board, banner appears at top.
            setBoard((rows) => rows.filter((r) => r.id !== job.id));
            setPendingUndo((rows) => [
                {
                    id: job.id, title: job.title,
                    deleted_at: new Date().toISOString(),
                    hard_delete_at: data?.hard_delete_at,
                },
                ...rows,
            ]);
            setSelected((prev) => { const n = new Set(prev); n.delete(job.id); return n; });
            toast.success("Job deleted — you have 24 hours to restore it from the banner above.");
            setDeleteTarget(null);
            setConfirmText("");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusyId(null); }
    };

    const restoreJob = async (jobId) => {
        setBusyId(jobId);
        try {
            await api.post(`/employer/jobs/${jobId}/undelete`);
            toast.success("Job restored to Drafts.");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusyId(null); }
    };

    // Bulk select helpers — scoped to the filtered view so "select all"
    // in Archived doesn't accidentally target Live jobs.
    const toggleSelect = (jobId) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(jobId)) n.delete(jobId); else n.add(jobId);
            return n;
        });
    };
    const toggleSelectAllVisible = (rows) => {
        setSelected((prev) => {
            const ids = rows.map((r) => r.id);
            const allSelected = ids.every((id) => prev.has(id));
            const n = new Set(prev);
            if (allSelected) { ids.forEach((id) => n.delete(id)); }
            else { ids.forEach((id) => n.add(id)); }
            return n;
        });
    };
    const clearSelection = () => setSelected(new Set());
    const bulkStatus = async (target) => {
        const ids = [...selected];
        if (ids.length === 0) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/employer/jobs/bulk-status", { job_ids: ids, status: target });
            toast.success(`Updated ${data?.updated ?? ids.length} job${(data?.updated ?? ids.length) === 1 ? "" : "s"} → ${target === "draft" ? "Draft" : "Archived"}.`);
            setBoard((rows) => rows.map((r) => ids.includes(r.id) ? { ...r, status: target } : r));
            setSelected(new Set());
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBulkBusy(false); }
    };

    const isFeatured = (job) => job.featured_until && new Date(job.featured_until) > new Date();

    if (loading) return <div data-tick={_tickTag} className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid="employer-jobs-page" data-tick={_tickTag} className="space-y-10">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Your roles</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Jobs</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Every role you&apos;ve uploaded — AI Hiring Briefs match against your talent library, and public Job Board postings appear on Google-for-Jobs once approved.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Link to="/employer/briefs#new" data-testid="jobs-post-brief" className="btn-outline text-sm">
                        <Sparkles size={13} className="mr-2" /> New AI Brief
                    </Link>
                    <Link to="/employer/jobs/new" data-testid="jobs-post-board" className="btn-green text-sm">
                        <Globe2 size={13} className="mr-2" /> Post to public Job Board
                    </Link>
                </div>
            </div>

            {/* Public Job Board postings */}
            <section data-testid="board-section">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h2 className="font-heading font-semibold text-lg text-foreground inline-flex items-center gap-2">
                        <Globe2 size={16} className="text-emerald-500" /> Public Job Board postings
                    </h2>
                    <span className="text-xs text-muted-foreground">{board.length} total</span>
                </div>

                {/* iter 149.4 — Pay-mode SEO analytics nudge. Only shows when
                    at least one DOE posting exists in the employer's live
                    board — keeps My Jobs quiet for employers who already
                    disclose ranges consistently. */}
                {payModeStats.doe > 0 && payModeStats.total > 0 && (
                    <div
                        className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4 flex items-start gap-3"
                        data-testid="jobs-paymode-analytics"
                    >
                        <div className="flex-none w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 flex items-center justify-center">
                            <Zap size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-heading font-semibold text-foreground">
                                Your ranges rank ~40% higher than DOE postings on Google
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                                You have{" "}
                                <strong className="text-foreground" data-testid="jobs-paymode-doe-count">{payModeStats.doe} DOE posting{payModeStats.doe === 1 ? "" : "s"}</strong>{" "}
                                out of {payModeStats.total} live. Google for Jobs and Simonara Search both weight postings with a disclosed range higher — swapping to a range is the single easiest visibility win.
                            </p>
                            <div className="mt-2 flex gap-3 text-[11px] text-muted-foreground flex-wrap">
                                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Range: <strong className="text-foreground">{payModeStats.range}</strong></span>
                                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" /> Starting at: <strong className="text-foreground">{payModeStats.starting_at}</strong></span>
                                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> DOE: <strong className="text-foreground">{payModeStats.doe}</strong></span>
                            </div>
                        </div>
                        <div className="flex-none">
                            <button
                                type="button"
                                onClick={() => setBulkPayOpen(true)}
                                className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                                data-testid="jobs-paymode-add-range-btn"
                            >
                                <Plus size={12} /> Add a range
                            </button>
                        </div>
                    </div>
                )}

                {/* 24h Undo banner — one card per soft-deleted job in grace. */}
                {pendingUndo.length > 0 && (
                    <div className="mb-4 space-y-2" data-testid="jobs-pending-undo">
                        {pendingUndo.map((p) => (
                            <div
                                key={p.id}
                                data-testid={`jobs-undo-banner-${p.id}`}
                                className="rounded-xl border border-amber-500/40 bg-amber-500/[0.08] p-4 flex items-center justify-between gap-3 flex-wrap"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-heading font-semibold text-foreground inline-flex items-center gap-2">
                                        <RotateCcw size={14} className="text-amber-600" />
                                        You deleted &ldquo;{p.title}&rdquo;
                                        <span
                                            title={p.hard_delete_at ? `Permanently deleted at ${new Date(p.hard_delete_at).toLocaleString()}` : undefined}
                                            data-testid={`jobs-undo-countdown-${p.id}`}
                                            className="text-[11px] font-mono tabular-nums px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-300"
                                        >
                                            {_fmtCountdown(p.hard_delete_at)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        Restore before the grace window ends or it&apos;ll be permanently removed with all applications.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => restoreJob(p.id)}
                                    disabled={busyId === p.id || (p.hard_delete_at && new Date(p.hard_delete_at).getTime() <= Date.now())}
                                    data-testid={`jobs-undo-restore-${p.id}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-500/15 disabled:opacity-60"
                                >
                                    <RotateCcw size={11} /> Restore
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {board.length > 0 && (
                    <div className="mb-4 flex flex-wrap gap-1.5" data-testid="board-filter-tabs">
                        {FILTER_TABS.map((t) => {
                            const n = counts[t.key] || 0;
                            const active = statusFilter === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => { setStatusFilter(t.key); setSelected(new Set()); }}
                                    data-testid={`board-filter-${t.key}`}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                        active
                                            ? "border-emerald-500 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-semibold"
                                            : "border-border bg-background hover:bg-muted text-muted-foreground"
                                    }`}
                                >
                                    {t.label} <span className="ml-1 tabular-nums opacity-70">{n}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Bulk-select toolbar — surfaces once anything is picked. */}
                {selected.size > 0 && (
                    <div
                        data-testid="bulk-toolbar"
                        className="mb-4 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
                    >
                        <div className="text-sm text-foreground">
                            <span data-testid="bulk-selected-count" className="font-semibold">{selected.size}</span> selected
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={() => toggleSelectAllVisible(filteredBoard)}
                                data-testid="bulk-select-all"
                                className="text-xs px-3 py-1.5 rounded-full border border-border bg-background hover:bg-muted text-muted-foreground"
                            >
                                Select all visible
                            </button>
                            <button
                                type="button"
                                onClick={() => bulkStatus("draft")}
                                disabled={bulkBusy}
                                data-testid="bulk-draft"
                                className="text-xs px-3 py-1.5 rounded-full border border-border bg-background hover:bg-muted text-muted-foreground inline-flex items-center gap-1 disabled:opacity-60"
                            >
                                <FileEdit size={11} /> Move to Draft
                            </button>
                            <button
                                type="button"
                                onClick={() => bulkStatus("archived")}
                                disabled={bulkBusy}
                                data-testid="bulk-archive"
                                className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/40 bg-white dark:bg-transparent text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 inline-flex items-center gap-1 disabled:opacity-60"
                            >
                                {bulkBusy ? <Loader2 size={11} className="animate-spin" /> : <Archive size={11} />} Archive selected
                            </button>
                            <button
                                type="button"
                                onClick={clearSelection}
                                data-testid="bulk-clear"
                                className="text-xs px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}

                {board.length === 0 ? (
                    <div data-testid="board-empty" className="card-soft p-8 text-center">
                        <Globe2 size={22} className="mx-auto text-emerald-500" />
                        <h3 className="mt-3 font-heading font-semibold text-base text-foreground">No Job Board postings yet.</h3>
                        <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
                            Post a role to the Simonara public Job Board — reviewed by our team, then promoted to Google-for-Jobs for organic reach.
                        </p>
                        <Link to="/employer/jobs/new" className="btn-green mt-5 inline-flex text-sm">
                            <Plus size={13} className="mr-2" /> Post your first role
                        </Link>
                    </div>
                ) : filteredBoard.length === 0 ? (
                    <div data-testid="board-filter-empty" className="rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center text-sm text-muted-foreground">
                        No jobs in <strong>{statusFilter}</strong>. <button onClick={() => setStatusFilter("all")} className="ml-2 text-brand-green hover:underline">Show all</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredBoard.map((j) => {
                            const status = j.status || "pending_review";
                            const canArchive = ["published", "draft", "pending_review", "rejected"].includes(status);
                            const canDraft = ["published", "archived", "rejected", "pending_review"].includes(status);
                            const disabled = busyId === j.id;
                            const isSelected = selected.has(j.id);
                            return (
                                <motion.div
                                    key={j.id}
                                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                    data-testid={`board-row-${j.id}`}
                                    className={`card-soft p-5 transition-colors ${isSelected ? "ring-2 ring-emerald-500/50" : ""}`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3 min-w-0 flex-1">
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={() => toggleSelect(j.id)}
                                                data-testid={`board-select-${j.id}`}
                                                aria-label={`Select ${j.title}`}
                                                className="mt-0.5 !rounded-[4px]"
                                            />
                                            <h3 className="font-heading font-semibold text-base text-foreground leading-snug min-w-0">{j.title}</h3>
                                        </div>
                                        <BoardStatusPill status={status} />
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                        {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {j.location}</span>}
                                        {j.employment_type && <Badge variant="outline" className="text-[10px]">{j.employment_type}</Badge>}
                                        {j.remote_status && j.remote_status !== "on_site" && (
                                            <Badge variant="outline" className="text-[10px] capitalize">{j.remote_status}</Badge>
                                        )}
                                    </div>
                                    {status === "rejected" && j.rejection_reason && (
                                        <p className="mt-3 rounded-md bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
                                            <strong>Simonara review notes:</strong> {j.rejection_reason}
                                        </p>
                                    )}
                                    <div className="mt-4 pt-3 border-t border-border flex items-center justify-between flex-wrap gap-2">
                                        <span className="text-[11px] text-muted-foreground">
                                            {j.posted_at ? new Date(j.posted_at).toLocaleDateString() : "recently"}
                                            {isFeatured(j) && (
                                                <Badge data-testid={`featured-badge-${j.id}`} className="ml-2 bg-amber-500/20 text-amber-700 dark:text-amber-400 border-0 text-[10px]">
                                                    <Zap size={9} className="inline mr-0.5" /> Featured
                                                </Badge>
                                            )}
                                        </span>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {status === "published" && !isFeatured(j) && (
                                                <button
                                                    onClick={() => promote(j.id)}
                                                    disabled={promoting === j.id || disabled}
                                                    data-testid={`board-promote-${j.id}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-amber-400/50 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 px-3 py-1 text-[11px] font-semibold hover:bg-amber-100 dark:hover:bg-amber-500/15 disabled:opacity-60"
                                                >
                                                    {promoting === j.id ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
                                                    Promote · $49
                                                </button>
                                            )}
                                            {status === "published" && (
                                                <Link
                                                    to={`/jobs/${j.slug || j.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`board-view-${j.id}`}
                                                    className="text-[11px] font-semibold text-brand-green hover:underline inline-flex items-center gap-1"
                                                >
                                                    View live <ArrowRight size={11} />
                                                </Link>
                                            )}
                                            {canDraft && (
                                                <button
                                                    onClick={() => setJobStatus(j, "draft")}
                                                    disabled={disabled}
                                                    data-testid={`board-draft-${j.id}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-border bg-background text-muted-foreground hover:bg-muted px-3 py-1 text-[11px] font-semibold disabled:opacity-60"
                                                    title="Move to Draft (hidden from public board)"
                                                >
                                                    <FileEdit size={10} /> Draft
                                                </button>
                                            )}
                                            {canArchive && (
                                                <button
                                                    onClick={() => setJobStatus(j, "archived")}
                                                    disabled={disabled}
                                                    data-testid={`board-archive-${j.id}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-border bg-background text-muted-foreground hover:bg-muted px-3 py-1 text-[11px] font-semibold disabled:opacity-60"
                                                    title="Archive (kept for records, hidden from public board)"
                                                >
                                                    <Archive size={10} /> Archive
                                                </button>
                                            )}
                                            <button
                                                onClick={() => requestDelete(j)}
                                                disabled={disabled}
                                                data-testid={`board-delete-${j.id}`}
                                                className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white dark:bg-transparent text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 px-3 py-1 text-[11px] font-semibold disabled:opacity-60"
                                                title="Delete permanently"
                                            >
                                                <Trash2 size={10} /> Delete
                                            </button>
                                            {/* iter-168 — Browse Talent entry point per role (spec §4) */}
                                            {j.status === "published" && (
                                                <Link
                                                    to={`/employer/jobs/${j.id}/browse`}
                                                    data-testid={`board-browse-${j.id}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 px-3 py-1 text-[11px] font-semibold"
                                                    title="Browse matched talent for this role"
                                                >
                                                    <Users2 size={10} /> Browse talent
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* AI Hiring Briefs */}
            <section data-testid="briefs-section">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading font-semibold text-lg text-foreground inline-flex items-center gap-2">
                        <Sparkles size={16} className="text-emerald-500" /> AI Hiring Briefs™
                    </h2>
                    <span className="text-xs text-muted-foreground">{briefs.length} total</span>
                </div>
                {briefs.length === 0 ? (
                    <div className="card-soft p-8 text-center">
                        <Briefcase size={22} className="mx-auto text-emerald-500" />
                        <h3 className="mt-3 font-heading font-semibold text-base text-foreground">No AI Briefs yet.</h3>
                        <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
                            Upload a JD — Simonara AI parses it, matches candidates in your library, and drafts a hiring brief in minutes.
                        </p>
                        <Link to="/employer/briefs#new" className="btn-outline mt-5 inline-flex text-sm">
                            Create Hiring Brief <Sparkles size={13} className="ml-2" />
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {briefs.map((b) => (
                            <motion.div
                                key={b.id}
                                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                data-testid={`job-brief-${b.id}`}
                                className="card-soft p-5"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <h3 className="font-heading font-semibold text-base text-foreground leading-snug">
                                        {b.job_profile?.title || "Analyzing…"}
                                    </h3>
                                    <StatusPill status={b.status || "processing"} />
                                </div>
                                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                    {b.job_profile?.company && <span className="inline-flex items-center gap-1"><Building2 size={11} /> {b.job_profile.company}</span>}
                                    {b.job_profile?.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {b.job_profile.location}</span>}
                                    {b.job_profile?.employment_type && <Badge variant="outline" className="text-[10px]">{b.job_profile.employment_type}</Badge>}
                                    {b.job_profile?.salary && <span className="inline-flex items-center gap-1"><DollarSign size={11} /> {b.job_profile.salary}</span>}
                                </div>
                                {b.summary && <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{b.summary}</p>}
                                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                                    <div className="text-[11px] text-muted-foreground">
                                        {(b.top_matches || []).length} candidate{(b.top_matches || []).length === 1 ? "" : "s"} matched
                                    </div>
                                    <Link to="/employer/briefs" className="text-xs font-semibold text-brand-green hover:underline inline-flex items-center gap-1">
                                        Open brief <ArrowRight size={11} />
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>

            {/* Type-DELETE-to-confirm dialog */}
            <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setConfirmText(""); } }}>
                <DialogContent data-testid="board-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete this job?</DialogTitle>
                        <DialogDescription>
                            This deletes &ldquo;{deleteTarget?.title}&rdquo; and hides it from the public board immediately.
                            You&apos;ll have <strong>24 hours to restore it</strong> from the banner on this page before it&apos;s permanently removed with all its applications.
                            To hide the role without the countdown, use <strong>Archive</strong> instead.
                            <br /><br />
                            Type <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">DELETE</code> to confirm.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        data-testid="board-delete-confirm-input"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="DELETE"
                        autoFocus
                    />
                    <DialogFooter className="gap-2">
                        <button
                            type="button"
                            onClick={() => { setDeleteTarget(null); setConfirmText(""); }}
                            data-testid="board-delete-cancel"
                            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={confirmDelete}
                            disabled={confirmText.trim() !== "DELETE" || busyId === deleteTarget?.id}
                            data-testid="board-delete-confirm"
                            className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                        >
                            <Trash2 size={12} /> Delete (24h undo)
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* iter 149.5 — Bulk "Add a range" modal. Lists every DOE
                posting from the live board with inline min/max inputs so
                the employer can convert them all in a single submit. */}
            <Dialog open={bulkPayOpen} onOpenChange={(open) => { if (!bulkPaySaving) setBulkPayOpen(open); }}>
                <DialogContent data-testid="bulk-pay-dialog" className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="inline-flex items-center gap-2">
                            <DollarSign size={18} className="text-emerald-500" />
                            Add a pay range to your DOE postings
                        </DialogTitle>
                        <DialogDescription>
                            Fill a min + max for each posting you want to convert. Blank rows are skipped — you can come back and finish the rest later. Simonara doesn&apos;t provide legal advice; check your local rules if unsure.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2 max-h-[50vh] overflow-y-auto space-y-2 pr-1" data-testid="bulk-pay-list">
                        {doeJobs.map((j) => {
                            const row = bulkPayRows[j.id] || {};
                            return (
                                <div key={j.id} className="rounded-lg border border-border p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`bulk-pay-row-${j.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-foreground truncate">{j.title}</div>
                                        <div className="text-[11px] text-muted-foreground truncate">{j.location}{j.employment_type ? ` · ${j.employment_type}` : ""}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="number"
                                            min="1"
                                            placeholder="Min"
                                            value={row.min || ""}
                                            onChange={(e) => setBulkPayRows((s) => ({ ...s, [j.id]: { ...s[j.id], min: e.target.value } }))}
                                            className="w-24"
                                            data-testid={`bulk-pay-min-${j.id}`}
                                        />
                                        <span className="text-xs text-muted-foreground">–</span>
                                        <Input
                                            type="number"
                                            min="1"
                                            placeholder="Max"
                                            value={row.max || ""}
                                            onChange={(e) => setBulkPayRows((s) => ({ ...s, [j.id]: { ...s[j.id], max: e.target.value } }))}
                                            className="w-24"
                                            data-testid={`bulk-pay-max-${j.id}`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                        {doeJobs.length === 0 && (
                            <div className="text-sm text-muted-foreground italic p-4 text-center">
                                No DOE postings — nice work. This card will disappear from your dashboard.
                            </div>
                        )}
                    </div>
                    <DialogFooter className="gap-2">
                        <button
                            type="button"
                            onClick={() => setBulkPayOpen(false)}
                            className="btn-outline text-sm"
                            data-testid="bulk-pay-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={saveBulkPay}
                            disabled={bulkPaySaving || doeJobs.length === 0}
                            className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                            data-testid="bulk-pay-save"
                        >
                            {bulkPaySaving
                                ? <><Loader2 size={13} className="animate-spin" /> Saving…</>
                                : <>Save ranges</>}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div>
    );
};

export default EmployerJobs;
